import React, { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@/utils/ProtectedRoute';
import { AdminProvider } from '@/contexts/AdminProvider';
import { CampaignProvider } from '@/contexts/CampaignProvider';
import { ChameleonProvider } from '@/contexts/ChameleonProvider';
import { GraphProvider } from '@/contexts/GraphProvider';
import { ModalProvider } from '@/contexts/ModalProvider';
import { SystemAdminProvider } from '@/contexts/SystemAdminProvider';
import { useAuth } from '@/contexts/AuthContext';
import { AppLayout } from '@/components/AppLayout';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { VersionBadge } from '@/components/VersionBadge';
import getVersionInfo from '@/utils/versionInfo';
import { isProduction } from '@/utils/environment';
import type { AuthState } from '@/types/auth';

const AcceptInvitationPage = lazy(() =>
    import('@/pages/auth/AcceptInvitationPage').then((module) => ({ default: module.AcceptInvitationPage }))
);
const AdminCentrePage = lazy(() =>
    import('@/pages/management/AdminCentrePage').then((module) => ({ default: module.AdminCentrePage }))
);
const CampaignsPage = lazy(() =>
    import('@/pages/campaigns/CampaignsPage').then((module) => ({ default: module.CampaignsPage }))
);
const DashboardPage = lazy(() =>
    import('@/pages/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage }))
);
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then((module) => ({ default: module.LoginPage })));
const OverviewPage = lazy(() =>
    import('@/pages/overview/OverviewPage').then((module) => ({ default: module.OverviewPage }))
);
const ResetPasswordPage = lazy(() =>
    import('@/pages/auth/ResetPasswordPage').then((module) => ({ default: module.ResetPasswordPage }))
);
// Outside production only; production never registers the route, so never fetches the chunk
const StyleSheetPage = lazy(() =>
    import('@/pages/style-sheet/StyleSheetPage').then((module) => ({ default: module.StyleSheetPage }))
);
const ChangelogPage = lazy(() =>
    import('@/pages/changelog/ChangelogPage').then((module) => ({ default: module.ChangelogPage }))
);
const SystemAdminPage = lazy(() =>
    import('@/pages/system-admin/SystemAdminPage').then((module) => ({ default: module.SystemAdminPage }))
);

const PageLoading = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'primary.light',
        }}
    >
        <CircularProgress size={60} thickness={4} sx={{ color: 'white' }} />
    </Box>
);

const PageBoundary = ({ scope, children }: { scope: string; children: React.ReactNode }) => (
    <ErrorBoundary scope={scope}>{children}</ErrorBoundary>
);

function ProtectedLayout({ authState }: { authState: AuthState }) {
    if (authState.loading) return <PageLoading />;

    return (
        <ProtectedRoute user={authState.user}>
            <ChameleonProvider>
                <SystemAdminProvider>
                    <AdminProvider>
                        <ModalProvider>
                            <CampaignProvider>
                                <GraphProvider>
                                    <AppLayout />
                                </GraphProvider>
                            </CampaignProvider>
                        </ModalProvider>
                    </AdminProvider>
                </SystemAdminProvider>
            </ChameleonProvider>
        </ProtectedRoute>
    );
}

function App() {
    const { authState } = useAuth();
    const { environment, version, buildDate, gitBranch, gitCommit } = getVersionInfo();
    const authRedirect = authState.isAuth ? <Navigate to="/overview" replace /> : <Navigate to="/login" replace />;

    return (
        <Router>
            <Suspense fallback={<PageLoading />}>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

                    <Route element={<ProtectedLayout authState={authState} />}>
                        <Route
                            path="/overview"
                            element={
                                <PageBoundary scope="the Overview page">
                                    <OverviewPage />
                                </PageBoundary>
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <PageBoundary scope="the Dashboard">
                                    <DashboardPage />
                                </PageBoundary>
                            }
                        />
                        <Route
                            path="/campaigns"
                            element={
                                <PageBoundary scope="the Setup page">
                                    <CampaignsPage />
                                </PageBoundary>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <PageBoundary scope="the Admin Centre">
                                    <AdminCentrePage />
                                </PageBoundary>
                            }
                        />
                        <Route
                            path="/system-admin"
                            element={
                                <PageBoundary scope="System Admin">
                                    <SystemAdminPage />
                                </PageBoundary>
                            }
                        />
                        {!isProduction && (
                            <Route
                                path="/style-sheet"
                                element={
                                    <PageBoundary scope="the style sheet">
                                        <StyleSheetPage />
                                    </PageBoundary>
                                }
                            />
                        )}
                        {!isProduction && (
                            <Route
                                path="/changelog"
                                element={
                                    <PageBoundary scope="the changelog">
                                        <ChangelogPage />
                                    </PageBoundary>
                                }
                            />
                        )}
                    </Route>

                    <Route path="/" element={authRedirect} />
                    <Route path="*" element={authRedirect} />
                </Routes>
            </Suspense>

            {!isProduction && (
                <VersionBadge
                    environment={environment}
                    version={version}
                    buildDate={buildDate}
                    gitBranch={gitBranch}
                    gitCommit={gitCommit}
                />
            )}
        </Router>
    );
}

export default App;
